import { type ReduxUser } from 'models';
import { useTranslation } from 'react-i18next';
import variables from 'common/styles/variables.json';
import { useUpdateUserProfile } from 'features/user-profile/queries/mutations';
import { type BirthDateType, formatBirthdayToString } from 'ui';
import {
  Col,
  Form,
  Modal,
  ModalFormActions,
  Paragraph,
  PhoneInput,
  Row,
  TextInput,
  BirthDateInput,
  getBirthDateTypeValue,
} from 'ui';

type P = {
  isOpened: boolean;
  onClose: () => void;
  onFinish: () => void;
  viewer: ReduxUser;
};

type TelehealthRequestPersonalInfoFormData = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  birthDate: BirthDateType;
};

const TelehealthRequestPersonalInfoModal = ({ isOpened, onClose, onFinish, viewer }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<TelehealthRequestPersonalInfoFormData>();
  const { mutate, isLoading } = useUpdateUserProfile();

  const initialValues = {
    firstName: viewer.first_name,
    lastName: viewer.last_name,
    email: viewer.email,
    phoneNumber: viewer.phone,
    birthDate: getBirthDateTypeValue(viewer.birthdate),
  };

  const onFormFinish = (data: TelehealthRequestPersonalInfoFormData) => {
    mutate(
      {
        userId: viewer.id,
        userData: {
          ...data,
          birthDate: formatBirthdayToString(data.birthDate),
        },
      },
      {
        onSuccess() {
          onFinish();
        },
      },
    );
  };

  return (
    <Modal
      title="Confirm your information"
      isOpened={isOpened}
      onCancel={() => {
        onClose();
      }}
      width="large"
      disableBack
      destroyOnClose
    >
      <Paragraph>
        Please confirm you are using your legal name and the information below is correct. After confirmation, any
        changes will be reflected in your profile
      </Paragraph>
      <Form<TelehealthRequestPersonalInfoFormData> onFinish={onFormFinish} form={form} initialValues={initialValues}>
        <Row item={{ gutter: variables.spaceMd.value }}>
          <Col item={{ span: 12 }}>
            <TextInput
              item={{
                name: 'firstName',
                label: 'First name',
                rules: [
                  {
                    required: true,
                    whitespace: true,
                  },
                ],
              }}
            />
          </Col>
          <Col item={{ span: 12 }}>
            <TextInput
              item={{
                name: 'lastName',
                label: 'Last name',
                rules: [
                  {
                    required: true,
                    whitespace: true,
                  },
                ],
              }}
            />
          </Col>
          <Col item={{ span: 24 }}>
            <PhoneInput
              item={{
                name: 'phoneNumber',
                label: 'Phone number',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col item={{ span: 24 }}>
            <BirthDateInput item={{ label: 'Date of birth', name: 'birthDate' }} />
          </Col>
        </Row>

        <ModalFormActions
          right={{ onClick: onClose, children: t('Back') }}
          submit={{
            children: t('Confirm'),
            loading: isLoading,
          }}
        />
      </Form>
    </Modal>
  );
};

export default TelehealthRequestPersonalInfoModal;
